import React, { useRef, useState } from "react";
import { ContextMenu } from "primereact/contextmenu";
import useTabId from "hooks/dashboard/useTabId";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import DashboardComponentCard from "./GroupComponentCard";
import DashboardComponentConfiguratorNew, {
  ConfiguratorModes,
} from "./GroupComponentConfiguratorNew";
import { updateDashboardTabs } from "../../utils";
import MoveGroupComponent from "./MoveGroupComponent";

interface DashboardComponentProps {
  groupId: string;
  componentId: string;
  component: IDashboardComponent;
  layoutType: DashboardLayoutType;
}

const DashboardComponent = ({
  groupId,
  componentId,
  component,
  layoutType,
}: DashboardComponentProps) => {
  const cm = useRef<ContextMenu>(null);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const dashboard = useDashboardStore(state => state.dashboard);
  const updateComponent = useDashboardStore(state => state.updateComponent);
  const deleteComponent = useDashboardStore(state => state.removeComponent);
  const tabId = useTabId();
  const [isConfiguratorVisible, setIsConfiguratorVisible] = useState<boolean>(false);
  const [isMoveComponentVisible, setIsMoveComponentVisible] = useState<boolean>(false);

  const handleDeleteComponent = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: "Confirm Action",
      message: `Are you sure you want to delete this component?`,
      confirmedCallback: () => {
        deleteComponent(groupId, componentId, tabId);
      },
    });
  };

  const handleDuplicateComponent = () => {
    if (!dashboard) return;
    const currentGroup = dashboard.dashboardTabsJs?.[tabId]?.componentGroups?.find(
      group => group.id === groupId
    );
    const componentToDuplicate = currentGroup?.components?.find(
      currentComponent => currentComponent.id === componentId
    );

    const componentToDuplicateWithNewId = {
      ...componentToDuplicate,
      id: uuidv4(),
    } as IDashboardComponent;
    currentGroup?.components?.push(componentToDuplicateWithNewId);

    if (!currentGroup || !componentToDuplicate) return;

    const updatedTabs = dashboard.dashboardTabsJs.map((tab, index) =>
      index === tabId
        ? {
            ...tab,
            componentGroups: tab.componentGroups.map(group =>
              group.id === groupId ? currentGroup : group
            ),
          }
        : tab
    );

    updateDashboardTabs(dashboard, updatedTabs);
  };

  const items = [
    {
      label: "Edit Config",
      icon: "pi pi-pencil",
      command: () => {
        setIsConfiguratorVisible(true);
      },
    },
    {
      label: "Duplicate Component",
      icon: "pi pi-clone",
      command: () => {
        easasConfirmDialog({
          icon: "pi pi-clone",
          acceptClassName: "p-button-success",
          header: "Confirm Action",
          message: `Are you sure you want to duplicate this component?`,
          confirmedCallback: () => {
            handleDuplicateComponent();
          },
        });
      },
    },
    {
      label: "Delete",
      icon: "pi pi-times",
      command: () => {
        handleDeleteComponent();
      },
    },
    {
      label: "Move component",
      icon: "pi pi-arrow-right",
      command: () => {
        setIsMoveComponentVisible(true);
      },
    },
  ];
  const handleComponentChange = (newConfig: IDashboardComponent) => {
    updateComponent(groupId, componentId, newConfig, tabId);
    setIsConfiguratorVisible(false);
  };
  return (
    <div className="h-full">
      {isEditMode && <ContextMenu model={items} ref={cm} />}
      <div onContextMenu={e => cm.current?.show(e)} className="h-full m-1">
        <DashboardComponentCard component={component} tabId={tabId} groupId={groupId} />
      </div>
      {isConfiguratorVisible && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={isConfiguratorVisible}
          mode={ConfiguratorModes.EDIT}
          setIsDialogVisible={setIsConfiguratorVisible}
          component={component}
          onComponentChange={handleComponentChange}
          layoutType={layoutType}
        />
      )}
      {isMoveComponentVisible && (
        <MoveGroupComponent
          isMoveComponentVisible={isMoveComponentVisible}
          setIsMoveComponentVisible={setIsMoveComponentVisible}
          tabId={tabId}
          groupId={groupId}
          componentId={componentId}
        />
      )}
    </div>
  );
};

export default DashboardComponent;
